import { Fragment, useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslate } from "@refinedev/core";

import { TableCell as DataCell } from "@shared/components";
import { IntegrationEndpoint, IntegrationHistoryAction, IntegrationHistoryEvent } from "@modules/erpIntegrations/types";
import { useListIntegrationHistoryQuery } from "@modules/erpIntegrations/services/queries";

const PAGE_SIZE = 10;

interface EndpointHistoryDialogProps {
  endpoint: IntegrationEndpoint | null;
  onClose: () => void;
}

function isOldNew(value: unknown): value is { old?: unknown; new?: unknown } {
  return value !== null && typeof value === "object" && !Array.isArray(value) && ("old" in value || "new" in value);
}

function HistoryValue({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === "") {
    return <Typography variant="body2">—</Typography>;
  }

  if (typeof value === "object") {
    return (
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1,
          fontSize: 12,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          bgcolor: "action.hover",
          borderRadius: 1,
          maxHeight: 240,
          overflow: "auto",
        }}
      >
        {JSON.stringify(value, null, 2)}
      </Box>
    );
  }

  return <Typography variant="body2">{String(value)}</Typography>;
}

function HistoryDetail({ event }: { event: IntegrationHistoryEvent }) {
  const translate = useTranslate();

  if (event.action === "update") {
    const entries = Object.entries(event.changes ?? {});
    if (entries.length === 0) {
      return <Typography variant="body2">{translate("erp_integrations.endpoint_history.empty_changes")}</Typography>;
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, py: 1 }}>
        {entries.map(([field, diff]) => (
          <Box key={field}>
            <Typography variant="caption" color="text.secondary">
              {field}
            </Typography>
            {isOldNew(diff) ? (
              <Grid container spacing={1}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption">
                    {translate("erp_integrations.endpoint_history.old")}
                  </Typography>
                  <HistoryValue value={diff.old} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption">
                    {translate("erp_integrations.endpoint_history.new")}
                  </Typography>
                  <HistoryValue value={diff.new} />
                </Grid>
              </Grid>
            ) : (
              <HistoryValue value={diff} />
            )}
          </Box>
        ))}
      </Box>
    );
  }

  const snapshot =
    event.snapshot && Object.keys(event.snapshot).length > 0
      ? event.snapshot
      : event.action === "create"
        ? event.changes?.created
        : event.changes?.deleted;

  return <HistoryValue value={snapshot ?? {}} />;
}

export function EndpointHistoryDialog({ endpoint, onClose }: EndpointHistoryDialogProps) {
  const translate = useTranslate();
  const [page, setPage] = useState(1);
  const [action, setAction] = useState<IntegrationHistoryAction | "">("");
  const [openId, setOpenId] = useState<number | null>(null);

  useEffect(() => {
    setPage(1);
    setAction("");
    setOpenId(null);
  }, [endpoint?.id]);

  const query = useListIntegrationHistoryQuery(
    {
      objectId: endpoint?.id ?? 0,
      action,
      page,
      perPage: PAGE_SIZE,
    },
    { enabled: Boolean(endpoint) }
  );

  const rows = query.data?.results ?? [];
  const count = query.data?.count ?? 0;

  const actor = (event: IntegrationHistoryEvent) =>
    event.changed_by_name || event.changed_by_email || translate("erp_integrations.endpoint_history.system");

  return (
    <Dialog open={Boolean(endpoint)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {translate("erp_integrations.endpoint_history.title")}
        {endpoint ? ` — ${endpoint.label}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          select
          size="small"
          label={translate("erp_integrations.endpoint_history.action")}
          value={action}
          onChange={(event) => {
            setAction(event.target.value as IntegrationHistoryAction | "");
            setPage(1);
            setOpenId(null);
          }}
          sx={{ mb: 2, minWidth: 200 }}
        >
          <MenuItem value="">{translate("erp_integrations.endpoint_history.all_actions")}</MenuItem>
          <MenuItem value="create">{translate("erp_integrations.endpoint_history.actions.create")}</MenuItem>
          <MenuItem value="update">{translate("erp_integrations.endpoint_history.actions.update")}</MenuItem>
          <MenuItem value="delete">{translate("erp_integrations.endpoint_history.actions.delete")}</MenuItem>
        </TextField>

        {query.isLoading && <CircularProgress size={20} />}

        {!query.isLoading && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {translate("erp_integrations.endpoint_history.empty")}
          </Typography>
        )}

        {rows.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate("erp_integrations.endpoint_history.date")}</TableCell>
                <TableCell>{translate("erp_integrations.endpoint_history.action")}</TableCell>
                <TableCell>{translate("erp_integrations.endpoint_history.who")}</TableCell>
                <TableCell>{translate("erp_integrations.endpoint_history.ip")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((event) => {
                const open = openId === event.id;
                return (
                  <Fragment key={event.id}>
                    <TableRow
                      hover
                      selected={open}
                      onClick={() => setOpenId(open ? null : event.id)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <DataCell.Date value={event.created_at} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={translate(`erp_integrations.endpoint_history.actions.${event.action}`)}
                          color={event.action === "delete" ? "error" : event.action === "create" ? "success" : "info"}
                        />
                      </TableCell>
                      <TableCell>{actor(event)}</TableCell>
                      <TableCell>{event.origin_ip || "—"}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={4} sx={{ py: 0, borderBottom: open ? undefined : "none" }}>
                        <Collapse in={open} unmountOnExit>
                          <HistoryDetail event={event} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}

        {count > PAGE_SIZE && (
          <TablePagination
            component="div"
            count={count}
            page={page - 1}
            onPageChange={(_, nextPage) => {
              setPage(nextPage + 1);
              setOpenId(null);
            }}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
